import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { useUser } from '/imports/hooks';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { PageFooter } from '/imports/ui/PageFooter';
import { Form } from '/imports/ui/Form';
import { FormField } from '/imports/ui/FormField';
import { Input } from '/imports/ui/Input';
import { Select } from '/imports/ui/Select';
import { toastOnError } from '/imports/ui/utils';
import {
  createFomwareCredential, getFomwareCredentialsForOrg, probeFomwareCredential,
  FomwareCredential, pullFomwareSites
} from '/imports/api/fomwareMeters';
import { FomwareApiVersion, fomwareApiVersions } from '/imports/api/fomwareMeters';

// FOMware ships two incompatible API generations; an account is on one or the other. v2 is the current
// one, so it is the default — Test reports when the paths belong to the other version.
const apiVersionOptions = fomwareApiVersions.map((v) => ({ value: v, label: v }));

const FomwareMeterPageStyled = styled(Page)`
  .hint { color: #666; margin-bottom: 1rem; }
  .buttons { display: flex; gap: 1rem; }
  table {
    td {
      padding: 1rem;
      &:first-child {
        padding-left: 0;
      }
    }
  }
  pre { overflow: auto; }
`;

/** Super-user page to manage FOMware credentials and pull their sites. */
export const FomwareMeterPage = () => {
  const user = useUser();
  if (!user?.isSuperUser) return <NotPermittedPage />;
  const orgId = user.orgId;
  const [baseUrl, setBaseUrl] = useState<string>('https://api.fomware.com');
  const [token, setToken] = useState<string>();
  const [apiVersion, setApiVersion] = useState<FomwareApiVersion>('v2');
  const [label, setLabel] = useState<string>();
  const [probe, setProbe] = useState<string>();
  const [credentials, setFomwareCredentials] = useState<FomwareCredential[]>([]);
  useEffect(() => {
    (async () => {
      if (!orgId) return;
      setFomwareCredentials(await getFomwareCredentialsForOrg.call(orgId));
    })();
  }, [orgId]);
  const pullSites = toastOnError(async (credentialId: string) => {
    const { numSystems, numCreated } = await pullFomwareSites.call(credentialId);
    toast.success(`${numSystems} ${strings.SystemsFound()} | ${numCreated} ${strings.Created()}`);
  });
  // Verify the credential works before storing it — a bad base URL or expired token otherwise fails
  // silently later, inside the nightly job.
  const test = toastOnError(async () => {
    if (!token) return;
    const res = await probeFomwareCredential.call({ baseUrl: baseUrl || undefined, apiVersion, token });
    setProbe(JSON.stringify(res, null, 2));
    if (res.ok) toast.success(`${res.sites} ${strings.SystemsFound()}`);
    else toast.error(res.message || strings.Error());
  });
  const save = toastOnError(async () => {
    if (!baseUrl || !token || !orgId) return;
    await createFomwareCredential.call({ baseUrl, apiVersion, token, label });
    setToken(undefined);
    setProbe(undefined);
    setFomwareCredentials(await getFomwareCredentialsForOrg.call(orgId));
  });
  return (
    <FomwareMeterPageStyled className="page">
      <PageHeading left={<h1>FOMware</h1>} />
      <PageBody>
        <div className="hint">{strings.FomwareBaseUrlTip()}</div>
        <Form>
          <div className="field-group">
            <FormField field="baseUrl">
              <label>{strings.BaseUrl()}</label>
              <Input value={baseUrl} onChange={(v) => setBaseUrl(v || '')} />
            </FormField>
            <FormField field="apiVersion">
              <label>{strings.ApiVersion()}</label>
              <Select value={apiVersion} options={apiVersionOptions} onChange={(v) => setApiVersion(v as FomwareApiVersion)} />
            </FormField>
            <FormField field="token">
              <label>{strings.ApiToken()}</label>
              <Input value={token} type="password" onChange={(v) => setToken(v)} />
            </FormField>
            <FormField field="label">
              <label>{strings.Label()}</label>
              <Input value={label} onChange={(v) => setLabel(v)} />
            </FormField>
          </div>
          <div className="buttons">
            <Button onClick={() => test()}>{strings.Test()}</Button>
            <Button onClick={() => save()}>{strings.Save()}</Button>
          </div>
        </Form>
        {probe && <pre>{probe}</pre> || null}
        {
          credentials.length ?
            <div>
              <h2>{strings.Accounts()}</h2>
              <table>
                <tbody>
                  {
                    credentials.map((c) => (
                      <tr key={c._id}>
                        <td>{c.label || c.baseUrl}</td>
                        <td>{c.apiVersion || 'v2'}</td>
                        <td>{c.expiresAt ? new Date(c.expiresAt).toLocaleDateString() : ''}</td>
                        <td><Button onClick={() => pullSites(c._id)}>{strings.PullSystems()}</Button></td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
            :
            null
        }
      </PageBody>
      <PageFooter />
    </FomwareMeterPageStyled>
  );
};
